
import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { IoMdArrowRoundBack } from "react-icons/io";
import NetworkIndicator from "../../../Practice/Components/NetworkIndicator.jsx";
import { useGetModuleByIdQuery, useGetQuestionLazyQuery } from "../../../../graphql/generated.tsx";
import Loader from "../../../../components/Loader";

const ModuleView = () => {
	const { id: moduleId } = useParams();
	const navigate = useNavigate();
	const { data, loading, error } = useGetModuleByIdQuery({
		variables: { id: moduleId },
		skip: !moduleId,
		fetchPolicy: 'network-only',
	});
	const [days, setDays] = useState([]);
	const [currentDayIndex, setCurrentDayIndex] = useState(0);
	const [clusterName, setClusterName] = useState("");
	const [description, setDescription] = useState("");
	const [domain, setDomain] = useState("");
	const [isLoadingQuestions, setIsLoadingQuestions] = useState(false);
	const [getQuestion] = useGetQuestionLazyQuery();

	// Fetch question details for each day (like in Module.jsx)
	const fetchQuestionDetails = async (questionId) => {
		try {
			const result = await getQuestion({
				variables: { id: questionId },
				fetchPolicy: 'network-only',
			});
			return result.data?.getQuestion;
		} catch {
			return null;
		}
	};

	const fetchDayQuestionDetails = async (questions) => {
		setIsLoadingQuestions(true);
		const questionDetails = [];
		try {
			const batchSize = 5;
			for (let i = 0; i < questions.length; i += batchSize) {
				const batch = questions.slice(i, i + batchSize);
				const promises = batch.map(q => fetchQuestionDetails(q.id));
				const results = await Promise.all(promises);
				questionDetails.push(...results.filter(q => q !== null));
			}
			return questionDetails;
		} finally {
			setIsLoadingQuestions(false);
		}
	};

	useEffect(() => {
		if (data?.getModuleById) {
			const module = data.getModuleById;
			setClusterName(module.name || '');
			setDescription(module.short_description || '');
			setDomain(module.domain || '');
			if (module.daywise_plan && module.daywise_plan.length > 0) {
				const daysData = module.daywise_plan.map(day => ({
					day_id: day.day_id,
					title: day.name,
					description: day.day_description,
					questions: day.questions.map(qid => ({ id: qid })),
				}));
				setDays(daysData);
				// Fetch question details for each day
				const fetchAllDaysDetails = async () => {
					for (let i = 0; i < daysData.length; i++) {
						const questionDetails = await fetchDayQuestionDetails(daysData[i].questions);
						if (questionDetails.length > 0) {
							setDays(prevDays => {
								const updatedDays = [...prevDays];
								updatedDays[i] = {
									...updatedDays[i],
									questions: questionDetails.map(q => ({
										id: q.id,
										title: q.title,
										description: q.question_sub_text || '',
										difficulty: q.difficulty,
										tags: q.tags || [],
										type: q.category?.name || '',
										topic: q.company_tags?.[0] || '',
									})),
								};
								return updatedDays;
							});
						}
					}
				};
				fetchAllDaysDetails();
			}
		}
	}, [data]);

	if (loading) return <div>Loading module data...</div>;
	if (error) return <div>Error loading module: {error.message}</div>;
	if (!data?.getModuleById) return <div>No module data found.</div>;

	return (
		<div className="w-full min-h-screen px-0 py-0 overflow-x-hidden bg-white max-w-screen">
			<NetworkIndicator />
			<div className="w-full px-2 py-6 mx-auto">
				<div className="flex items-center mb-6">
					<button
						onClick={() => navigate('/admin/module')}
						className="flex items-center text-gray-500 hover:text-gray-700 focus:outline-none"
						title="Back to Module List"
					>
						<IoMdArrowRoundBack className="w-6 h-6" />
						<span className="ml-1 text-base font-medium">Go Back</span>
					</button>
				</div>
				<div className="flex items-center justify-center w-full">
					<div className="flex flex-col items-center w-full max-w-md gap-4 mt-4">
						<input
							type="text"
							value={clusterName}
							readOnly
							className="w-full px-3 py-2 bg-gray-100 border border-gray-300 rounded"
						/>
						<textarea
							value={description}
							readOnly
							rows={4}
							className="w-full px-3 py-2 bg-gray-100 border border-gray-300 rounded"
						/>
						<input
							type="text"
							value={domain}
							readOnly
							className="w-full px-3 py-2 bg-gray-100 border border-gray-300 rounded"
						/>
					</div>
				</div>

				{days.length > 0 && (
					<>
						<div className="flex w-full min-h-[1000px] bg-white rounded-lg shadow border border-gray-200 pl-5 mt-8">
							{/* Left: Cluster name and Day boxes */}
							<div className="flex flex-col items-center mr-6" style={{ minWidth: '120px', maxWidth: '160px', width: '140px' }}>
								<div className="flex items-center justify-center h-10 px-10 mt-6 mb-4" style={{ width: 'auto', minWidth: '120px' }}>
									
								</div>
								<div className="flex flex-wrap justify-center w-full gap-2">
									{days.map((day, idx) => (
										<React.Fragment key={idx}>
											<button
												type="button"
												onClick={() => setCurrentDayIndex(idx)}
												style={{ backgroundColor: idx === currentDayIndex ? "rgba(14,61,93,1)" : "#e5e7eb" }}
												className={`
													h-10
													px-4
													border border-gray-200
													rounded-lg
													transition-all duration-150
													${idx === currentDayIndex ? "text-white" : "text-gray-800 hover:bg-blue-100"}
													cursor-pointer
													w-full sm:w-auto
												`}
											>
												{day.title && day.title.trim().length > 0 ? `${day.title}` : `Day ${idx + 1}`}
											</button>
										</React.Fragment>
									))}
								</div>
							</div>
							{/* Main content area (full width minus left box) */}
							<div className="flex-1 bg-white rounded-lg shadow-inner">
								<div className="flex flex-col items-center w-full mb-4 bg-white" style={{ position: "relative" }}>
									{/* Day title & description inputs */}
									<div className="w-full max-w-3xl p-4">
										<div className="flex items-center gap-3 mb-3">
											<input
												type="text"
												value={days[currentDayIndex]?.title || ""}
												readOnly
												className="flex-1 px-3 py-2 bg-gray-100 border border-gray-300 rounded"
											/>
										</div>
										<div>
											<textarea
												value={days[currentDayIndex]?.description || ""}
												readOnly
												className="w-full px-3 py-2 bg-gray-100 border border-gray-300 rounded"
												rows={2}
											/>
										</div>
									</div>

									{days[currentDayIndex]?.questions?.length > 0 && (
										<div className="flex flex-col items-center w-full mb-6 space-y-4">
											<div className="flex flex-col items-center w-full max-w-2xl">
												{days[currentDayIndex].questions.map((question) => (
													<div
														key={question.id}
														className="flex items-center w-full max-w-lg p-3 my-2 shadow-lg rounded-xl"
														style={{
															minWidth: '260px',
															maxWidth: '32rem',
															background: 'rgba(255,255,255,0.92)',
															backdropFilter: 'blur(3px)',
															WebkitBackdropFilter: 'blur(3px)',
															border: '1px solid rgba(255,255,255,0.18)',
														}}
													>
														<div className="flex flex-col flex-1 gap-2 pr-4">
															<span className="text-base font-medium text-gray-800" title={question.title}>
																{question.title}
															</span>
															{question.tags && question.tags.length > 0 && (
																<div className="flex flex-wrap gap-1 mt-1">
																	{question.tags.map((tag, i) => (
																		<span key={i} className="px-3 py-1 text-xs text-blue-800 bg-blue-100 rounded-full">
																			{tag}
																		</span>
																	))}
																</div>
															)}
															{question.difficulty && (
																<span
																	className={`mt-1 px-2 py-0.5 text-xs rounded-full w-fit
																		${question.difficulty === 'EASY' ? 'bg-green-100 text-green-800' : ''}
																		${question.difficulty === 'MEDIUM' ? 'bg-yellow-100 text-yellow-800' : ''}
																		${question.difficulty === 'HARD' ? 'bg-red-100 text-red-800' : ''}
																	`}
																>
																	{question.difficulty}
																</span>
															)}
															{question.type && <span className="px-3 py-1 mt-2 text-sm text-yellow-800 bg-yellow-100 rounded-full w-fit">{question.type}</span>}
															{question.topic && <span className="px-3 py-1 mt-2 text-sm text-purple-800 bg-purple-100 rounded-full w-fit">{question.topic}</span>}
															{question.level && <span className="px-3 py-1 mt-2 text-pink-800 bg-pink-100 rounded-full text- w-fit">{question.level}</span>}
														</div>
													</div>
												))}
											</div>
										</div>
									)}
									{isLoadingQuestions && (
										<div className="flex items-center justify-center w-full py-8">
											<div className="flex flex-col items-center">
												<Loader />
												<p className="mt-3 text-gray-600">Loading question details...</p>
											</div>
										</div>
									)}
								</div>
							</div>
						</div>
					</>
				)}
			</div>
		</div>
	);
};

export default ModuleView;
